import { last, map } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { Breadcrumbs, Button, FullHeightLoader, Loader, TextButton, WixIcons } from '../../components/common';
import { SmallListFolder } from '../../components/layouts/List/SmallListFolder';
import { MainSource } from '../../constants/Source';
import { ClickOrigin } from '../../utils/hoc/bi/withTrackSiteFilesAnalytics/withTrackSiteFilesAnalytics.types';
import { useTrackSiteFilesAnalytics } from '../../utils/hooks/bi/useTrackSiteFilesAnalytics';
import { useMediaManagerSDK } from '../../utils/hooks/useMediaManagerSDK';
import { canPasteItemsFromTo } from '../../utils/privateMedia/folder';
import { uniqueFolderId } from '../../utils/uniqueId';
import styles from './ModalFilesManager.scss';
import { IInner, IOuter } from './ModalFilesManager.types';

/**
 * TODO this component has a lot similar (breadcrumbs, infinite scroll, footer, loaders, styles) with Files Importer.
 * It should be separated to component. https://jira.wixpress.com/browse/MMGR6-402
 */

export const ModalFilesManagerComponent: FC<IOuter & IInner> = ({
  items,
  isLoading,
  hasMoreItems,
  areNextFilesLoading,
  folderPath,
  source,
  openFolder,
  editingItem,
  createNewFolderStart,
  isItemEditing,
  loadMoreItems,
  onFolderCreate,
  onMoveClick,
  sourceItems,
  currentFolder,
  openJustCreatedFolder,
  createNewFolderComplete,
  createNewFolderCancel,
  updateEditingItem,
  editItemComplete,
}) => {
  const { createNewFolderMP } = useMediaManagerSDK({ source: MainSource.SiteFiles, currentFolder });
  const { trackFolderCreateStart } = useTrackSiteFilesAnalytics();

  const canMoveHere = canPasteItemsFromTo(folderPath, sourceItems);

  const handleCreateNewFolderStart = useCallback(() => {
    const id = uniqueFolderId();

    createNewFolderStart(id);
    trackFolderCreateStart(ClickOrigin.MoveTo);
  }, [createNewFolderStart, trackFolderCreateStart]);

  const createNewFolder = useCallback(
    (name) => {
      createNewFolderMP({ name, parentId: currentFolder.id }).then(({ data }) => {
        createNewFolderComplete(data);
        openJustCreatedFolder(editingItem.id, data);
        onFolderCreate(data);
      });
    },
    [currentFolder, createNewFolderMP, editingItem, createNewFolderComplete, openJustCreatedFolder, onFolderCreate],
  );

  const handleNameChange = useCallback((name) => updateEditingItem({ name }), [updateEditingItem]);
  const moveToFolder = useCallback(() => onMoveClick(last(folderPath)), [onMoveClick, folderPath]);

  const completeEditingItem = useCallback(() => {
    createNewFolder(editingItem.name);
    editItemComplete();
  }, [editingItem, editItemComplete, createNewFolder]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs
          className={styles.breadcrumbs}
          folderPath={folderPath}
          source={source}
          onClick={openFolder}
          dropDisabled
        />
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loader}>
            <FullHeightLoader />
          </div>
        ) : (
          <InfiniteScroll
            scrollThreshold="400px"
            height="100%"
            dataLength={items.length}
            hasMore={hasMoreItems}
            next={loadMoreItems}
            loader={null}
          >
            <ul className={styles.itemsList}>
              {map((item) => {
                const isEditing = isItemEditing(item.id);
                return (
                  <SmallListFolder
                    key={item.id}
                    className={styles.folder}
                    item={isEditing ? editingItem : item}
                    onClick={isEditing ? noop : openFolder}
                    isEditing={isEditing}
                    showCheckbox={false}
                    onNameChange={handleNameChange}
                    onRenameComplete={completeEditingItem}
                    onRenameCancel={createNewFolderCancel}
                  />
                );
              }, items)}
            </ul>

            {areNextFilesLoading && (
              <div className={styles.loaderMore}>
                <Loader size="small" />
              </div>
            )}
          </InfiniteScroll>
        )}
      </div>

      <div className={styles.footer}>
        <TextButton
          as="a"
          prefixIcon={<WixIcons.NewFolderSmall />}
          onClick={handleCreateNewFolderStart}
          dataHook="move-to-create-folder"
        >
          <FormattedMessage id="actions.create-new-folder" />
        </TextButton>

        <Button size="small" onClick={moveToFolder} disabled={!canMoveHere} dataHook="move-to-move-here">
          <FormattedMessage id="actions.move-here" />
        </Button>
      </div>
    </div>
  );
};
